import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../../services/api";

const UNASSIGNED = "";

type ExplorerEpisodeProgramEditDialogProps = {
  open: boolean;
  onClose: () => void;
  podcastId: string;
  contentId: string;
  contentTitle: string;
  currentProgramId: string;
};

export const ExplorerEpisodeProgramEditDialog: React.FC<
  ExplorerEpisodeProgramEditDialogProps
> = ({
  open,
  onClose,
  podcastId,
  contentId,
  contentTitle,
  currentProgramId,
}) => {
  const { t } = useTranslation("explorer");
  const queryClient = useQueryClient();
  const [selectedProgramId, setSelectedProgramId] = useState(currentProgramId);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedProgramId(currentProgramId);
    setSaveError(null);
  }, [open, currentProgramId, contentId]);

  const programsQuery = useQuery({
    queryKey: ["podcast-programs", podcastId],
    queryFn: () => apiService.getPodcastPrograms(podcastId),
    enabled: open && !!podcastId,
    staleTime: 5 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: (programId: string | null) =>
      apiService.patchAdminChannelContentProgram(podcastId, contentId, programId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["explorer-program-episodes"] });
      await queryClient.invalidateQueries({ queryKey: ["explorer-program-detail"] });
      await queryClient.invalidateQueries({ queryKey: ["explorer-program-trends"] });
      onClose();
    },
    onError: () => setSaveError(t("error")),
  });

  const programs = [...(programsQuery.data?.programs ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  const unchanged = selectedProgramId === currentProgramId;

  return (
    <Dialog open={open} onClose={saveMutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("editProgram")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {contentTitle}
        </Typography>
        {saveError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        ) : null}
        <FormControl fullWidth disabled={programsQuery.isLoading || saveMutation.isPending}>
          <InputLabel id="episode-program-edit-label">{t("editProgram")}</InputLabel>
          <Select
            labelId="episode-program-edit-label"
            label={t("editProgram")}
            value={selectedProgramId}
            onChange={(event) => setSelectedProgramId(event.target.value)}
          >
            <MenuItem value={UNASSIGNED}>
              <em>{t("noProgram")}</em>
            </MenuItem>
            {programs.map((program) => (
              <MenuItem key={program._id} value={program._id}>
                {program.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMutation.isPending}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={unchanged || programsQuery.isLoading || saveMutation.isPending}
          onClick={() => {
            setSaveError(null);
            saveMutation.mutate(selectedProgramId === UNASSIGNED ? null : selectedProgramId);
          }}
        >
          {saveMutation.isPending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
